import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sendCulturalElementChatMessage } from '../utils/culturalElementChatApi';
import { track } from '../utils/analytics';
import styles from './LessonPlanAIChat.module.css';

/**
 * AI Chat component for creating custom cultural elements
 * Allows teachers to chat with AI to create custom stories, proverbs, or cultural practices
 */
function CulturalElementChat({ 
  lessonPlanId, 
  grade,
  standard,
  language,
  moku,
  island,
  existingElements,
  onElementAccepted,
  onAddAnother
}) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingElement, setPendingElement] = useState(null);
  
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send initial greeting message on mount
  useEffect(() => {
    if (messages.length === 0) {
      const greeting = {
        role: 'assistant',
        content: "Aloha! I'm here to help you create a custom cultural element for your lesson plan. Please tell me about the story (moʻolelo), proverb (ʻōlelo noʻeau), or cultural practice you'd like to use, and I'll help you connect it to your standard.",
        timestamp: new Date().toISOString()
      };
      setMessages([greeting]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    track('Clicked Send (Kokua Chat)', { lesson_plan_id: lessonPlanId, screen_name: 'Select Elements' });
    setInputMessage('');
    setError(null);
    setPendingElement(null);

    // Add user message to chat
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    
    // Update messages state immediately for UI
    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);

    setLoading(true);

    try {
      // Send message to API - pass current messages (backend will add the new message separately)
      // Following the same pattern as LessonPlanAIChat for consistency
      const response = await sendCulturalElementChatMessage(
        lessonPlanId,
        userMessage,
        messages, // Pass current messages (before the new user message is added in backend)
        grade,
        standard,
        language,
        moku,
        island,
        existingElements
      );

      // Add AI response to chat
      const aiMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date().toISOString(),
        culturalElement: (response.has_cultural_element && response.cultural_element_data) ? {
          type: response.cultural_element_type,
          data: response.cultural_element_data
        } : null
      };
      setMessages(prev => [...prev, aiMessage]);

      // If there's a cultural element with valid data, set it as pending
      if (response.has_cultural_element && response.cultural_element_data) {
        setPendingElement({
          type: response.cultural_element_type,
          data: response.cultural_element_data
        });
      } else {
        // Clear pending element if data is invalid
        setPendingElement(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to send message');
      console.error('Chat error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptElement = () => {
    if (pendingElement && onElementAccepted) {
      onElementAccepted(pendingElement);
      // Clear pending element but keep messages and sidebar open
      // so user can continue adding more elements
      setPendingElement(null);
      // Clear the input so user can type a new message
      setInputMessage('');
    }
  };

  const handleAddAnother = () => {
    if (onAddAnother) {
      onAddAnother();
      setMessages([]);
      setPendingElement(null);
      setInputMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderElementPreview = (element) => {
    if (!element || !element.data) return null;

    const { type, data } = element;

    return (
      <div className={styles.elementPreview}>
        <div className={styles.elementPreviewHeader}>
          <h4>
            {type === 'story' ? 'Moʻolelo' : 
             type === 'proverb' ? 'ʻŌlelo Noʻeau' : 
             'Cultural Practice'}
          </h4>
        </div>
        <div className={styles.elementPreviewContent}>
          {type === 'story' && (
            <>
              {data.mo_olelo_name && (
                <p><strong>Name:</strong> {data.mo_olelo_name}</p>
              )}
              {data.mo_olelo && (
                <p><strong>Story:</strong> {data.mo_olelo}</p>
              )}
            </>
          )}
          {type === 'proverb' && (
            <>
              {data.olelo_noeau && (
                <p><strong>Proverb:</strong> {data.olelo_noeau}</p>
              )}
              {data.translation && (
                <p><strong>Translation:</strong> {data.translation}</p>
              )}
            </>
          )}
          {type === 'cultural_practice' && (
            <>
              {data.cultural_practice_name && (
                <p><strong>Name:</strong> {data.cultural_practice_name}</p>
              )}
              {data.full_cultural_practice && (
                <p><strong>Practice:</strong> {data.full_cultural_practice}</p>
              )}
            </>
          )}
          {data.connection && (
            <div className={styles.connectionPreview}>
              <p><strong>Connection:</strong> {data.connection}</p>
            </div>
          )}
        </div>
        <div className={styles.elementPreviewActions}>
          <button 
            className={styles.acceptButton}
            onClick={handleAcceptElement}
          >
            Accept
          </button>
          <button 
            className={styles.addAnotherButton}
            onClick={handleAddAnother}
          >
            Add Another
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.chatContainer}>
      {/* Messages area */}
      <div className={styles.messagesArea}>
        {messages.map((msg, idx) => (
          <div 
            key={idx} 
            className={`${styles.message} ${styles[msg.role]}`}
          >
            {msg.role === 'user' && (
              <div className={styles.messageHeader}>
                <span className={styles.role}>You</span>
              </div>
            )}
            {msg.role === 'assistant' && (
              <div className={styles.messageHeader}>
                <span className={styles.role}>AI Assistant</span>
              </div>
            )}
            {msg.role !== 'system' && (
              <div className={styles.messageContent}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {msg.content}
                </ReactMarkdown>
              </div>
            )}
            
            {/* Show cultural element preview if present */}
            {msg.culturalElement && (
              renderElementPreview(msg.culturalElement)
            )}
          </div>
        ))}

        {/* Show pending element if available and not already shown in a message */}
        {pendingElement && !messages.some(msg => msg.culturalElement && 
          JSON.stringify(msg.culturalElement) === JSON.stringify(pendingElement)) && (
          <div className={styles.message}>
            {renderElementPreview({ type: pendingElement.type, data: pendingElement.data })}
          </div>
        )}

        {loading && (
          <div className={styles.loadingIndicator}>
            <div className={styles.spinner}></div>
            <span>AI is thinking...</span>
          </div>
        )}

        {error && (
          <div className={styles.errorMessage}>
            <span className={styles.errorIcon}>⚠️</span>
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className={styles.inputArea}>
        <textarea
          className={styles.input}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Tell me about your story, proverb, or cultural practice..."
          rows={3}
          disabled={loading}
        />
        <button 
          className={styles.sendButton}
          onClick={handleSendMessage}
          disabled={loading || !inputMessage.trim()}
        >
          {loading ? '...' : 'Send'}
        </button>
      </div>
    </div>
  );
}

export default CulturalElementChat;
